/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * DashboardCard
 *
 * @file DashboardCard.jsx
 * @author Gladson Samuel S
 * @since 1.0.0
 */

import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, Typography, Badge } from '@material-ui/core';
import CustomCard from '../common/CustomCard';
import DashboardCardStyles from './styles/DashboardCardStyles';
import Icon from '../icons/Icon';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import FooterDeatilsCard from '../common/FooterDeatilsCard';
import SubscriptionStatusBtn from '../subscriptionStatusBtn/SubscriptionStatusBtn';

const DashboardCard = (props) => {
  const {
    title,
    subTitle,
    footer,
    actions,
    children,
    config,
    onBackNavClick,
    handleTitleClick,
    footerDialogDetails,
    subscriptionText,
    onInit = () => {}
  } = props;
  const customCardContainerRef = useRef(null);
  const childContainerRef = useRef(null);

  const [isFooterDialogOpen, setFooterDialogOpen] = useState(false);

  useEffect(() => {
    onInit();
  }, []);

  useEffect(() => {
    const rootEl = customCardContainerRef.current;
    const childEl = childContainerRef.current;
    if (!rootEl || !childEl) return;

    const rootStyle = document.documentElement.style;

    let rootHeight = 0;
    let childHeight = 0;

    const calculateTitleHeight = () => {
      const topPosition = Math.max(0, rootHeight - childHeight);
      const value = `-${Math.floor(topPosition)}px`;
      const current = rootStyle.getPropertyValue('--slick-dots-position');

      if (current !== value) {
        rootStyle.setProperty('--slick-dots-position', value);
      }
    };

    // initial values
    rootHeight = rootEl.offsetHeight;
    childHeight = childEl.offsetHeight;

    calculateTitleHeight();

    const observer = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        entries.forEach((entry) => {
          if (entry.target === rootEl) {
            rootHeight = entry.contentRect.height;
          }
          if (entry.target === childEl) {
            childHeight = entry.contentRect.height;
          }
        });

        calculateTitleHeight();
      });
    });

    observer.observe(rootEl);
    observer.observe(childEl);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.unobserve(rootEl);
      observer.unobserve(childEl);
    };
  }, []);

  const openFooterDialog = () => {
    setFooterDialogOpen(true);
  };

  const closeFooterDialog = () => {
    setFooterDialogOpen(false);
  };

  const classes = DashboardCardStyles(props);

  const {
    left,
    right
  } = actions || {};

  const {
    showHeader = true,
    showBackNav = false,
    enableTitleClick = false,
    showCursorPointer = false,
    showSubscriptionStatus = false
  } = config || {};


  const renderTitle = () => {
    if (!title) {
      return null;
    }

    return (
      <Typography
        className={`${classes.title} ${showCursorPointer ? classes.showCursorPointer : ''}`}
        onClick={enableTitleClick ? handleTitleClick : undefined}
      >
        {title}
      </Typography>
    );
  };

  const renderAction = (action) => {
    if (action.type === 'custom' && action.element) {
      return <div>{action.element}</div>;
    }
    if (!action.callback) {
      return null;
    }

    if (action.iconName && !action.text) {
      return (
        <TooltipWrapper tooltip={action.tooltip || ''} placement="top">
          <Badge
            badgeContent={action.badgeCount || 0}
            showZero={false}
            color="secondary"
            overlap="circular"
            classes={{
              root: classes.badgeRoot,
              badge: classes.badge
            }}
          >
            <IconButton className={`${classes.iconButton} ${classes.iconOnlyButton} ${action.badgeCount > 0 ? classes.circleAroundIcon : ''}`} onClick={(event) => (action.disabled ? () => {} : action.callback(action, event))} disabled={action.disabled}>
              <Icon name={action.iconName} className={classes.icon} />
            </IconButton>
          </Badge>
        </TooltipWrapper>
      );
    }

    if (action.iconName && action.text) {
      return (
        <Button
          className={classes.iconButton}
          onClick={() => action.callback(action)}
          startIcon={<Icon name={action.iconName} className={classes.icon} />}
        >
          {action.text}
        </Button>
      );
    }

    return (
      <Button className={classes.button} onClick={() => action.callback(action)}>{action.text}</Button>
    );
  };

  const renderActions = (argActions, className) => {
    if (!argActions) {
      return null;
    }

    return (
      <div className={`${classes.actions} ${className}`}>{argActions.map((action) => renderAction(action))}</div>
    );
  };

  const renderSubTitle = () => {
    if (!subTitle) {
      return null;
    }

    return (
      <Typography className={classes.subTitle}>{subTitle}</Typography>
    );
  };

  const renderFooter = () => {
    if (!footer) return null;
    const shouldShowFooterDialog =
    footerDialogDetails &&
    (footerDialogDetails.title || footerDialogDetails.content);

    return (
      <>
        <Typography className={classes.footer}>
          {footer}
          {shouldShowFooterDialog && (
          <IconButton
            aria-label="More information"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openFooterDialog();
            }}
          >
            <Icon name="FooterInfoIcon" style={{ fontSize: 16 }} />
          </IconButton>
          )}
        </Typography>
        {shouldShowFooterDialog && (
        <FooterDeatilsCard
          open={isFooterDialogOpen}
          onClose={closeFooterDialog}
          title={footerDialogDetails.title}
          content={footerDialogDetails.content}
          confirmText={footerDialogDetails.confirmText || 'Confirm'}
          showCloseButton={footerDialogDetails.showCloseButton ?? true}
          onConfirm={footerDialogDetails.onConfirm || closeFooterDialog}
        />
        )}
      </>
    );
  };

  const renderSubscriptionStatusBtn = () => {
    if (!showSubscriptionStatus) {
      return null;
    }

    return (
      <SubscriptionStatusBtn
        text={subscriptionText}
      />
    );
  };

  return (
    <CustomCard className={classes.root} ref={customCardContainerRef}>
      {showHeader ? (
        <div className={classes.header}>
          <div className={classes.actionsLeftContainer}>
            {showBackNav && (
              <IconButton className={classes.iconButtonMedium} onClick={onBackNavClick}>
                <Icon name="BackIcon" className={classes.iconMedium} />
              </IconButton>
            )}
            {renderTitle()}
            {renderSubscriptionStatusBtn()}
            {renderActions(left, classes.actionsLeft)}
          </div>
          {renderActions(right, classes.actionsRight)}
        </div>
      ) : null}
      {renderSubTitle()}
      <div className={classes.content} ref={childContainerRef}>
        {children}
      </div>
      {renderFooter()}
    </CustomCard>
  );
};

DashboardCard.propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string,
  footer: PropTypes.string,
  children: PropTypes.node.isRequired,
  actions: PropTypes.object,
  subscriptionText: PropTypes.string,
  config: PropTypes.object,
  onBackNavClick: PropTypes.func,
  handleTitleClick: PropTypes.func,
  onInit: PropTypes.func,
  footerDialogDetails: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    showCloseButton: PropTypes.bool,
    onConfirm: PropTypes.func
  })
};

DashboardCard.defaultProps = {
  subTitle: '',
  footer: '',
  actions: {},
  subscriptionText: '',
  config: {
    showHeader: true,
    showBackNav: false,
    showSubscriptionStatus: false
  },
  onBackNavClick: () => {},
  handleTitleClick: () => {},
  onInit: () => {},
  footerDialogDetails: {
    title: '',
    content: '',
    confirmText: 'Confirm',
    cancelText: null,
    showCloseButton: false,
    onConfirm: () => {}
  }
};


export default DashboardCard;
